var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { StackActions } from '@react-navigation/native';
import { getNavigationContainerRef } from '../../Hooks/UseNavigatorRef';
import { getPrevRoute, getRoutes, getScreen, submitRouteParams } from '../../Hooks/UseRouteParams';
import { Observable } from '../../../Data/Observable/Observable';
import { Platform } from 'react-native';
import queryString from 'query-string';
import { Application } from '../../../Application';
let isLoadedNavigation = false;
const dismissMiddlewares = new Map();
export class BaseCoordinator {
    constructor() {
        this.allRoutes = [];
        this.waitingScreens = new Observable([]);
        this.isReadyListeners = [];
        this.routeChangeCallbacks = [];
        let prevNavListener = () => { };
        getNavigationContainerRef().observe(this, navigationContainer => {
            var _a, _b;
            if (!navigationContainer) {
                return;
            }
            if (this.navigationContainer === navigationContainer) {
                return;
            }
            this.navigationContainer = navigationContainer;
            prevNavListener();
            prevNavListener = this.navigationContainer.addListener('state', () => {
                const params = this.getCurrentRouteParams();
                this.routeChangeCallbacks.forEach(listener => listener(params));
            });
            if (!isLoadedNavigation) {
                isLoadedNavigation = true;
                this.loadState((_a = this.navigationContainer) === null || _a === void 0 ? void 0 : _a.getState());
            }
            const listener = (_b = this.navigationContainer) === null || _b === void 0 ? void 0 : _b.addListener('state', () => {
                var _a;
                if ((_a = this.navigationContainer) === null || _a === void 0 ? void 0 : _a.isReady()) {
                    listener === null || listener === void 0 ? void 0 : listener();
                    this.executeWaitingScreens();
                    this.isReadyListeners.forEach(fn => fn());
                }
            });
        });
        getRoutes().observe(this, allRoutes => {
            this.allRoutes = allRoutes;
        });
    }
    resetState(state) {
        return Promise.resolve(getNavigationContainerRef().value.reset(state));
    }
    getRootState() {
        return getNavigationContainerRef().value.getRootState();
    }
    onRouteChange(fn) {
        this.routeChangeCallbacks.push(fn);
        return () => {
            this.routeChangeCallbacks = this.routeChangeCallbacks.filter(listener => listener !== fn);
        };
    }
    isModalCurrentRoute() {
        return this.isModal(this.getCurrentRoute());
    }
    isModal(route) {
        var _a, _b, _c, _d;
        if (!route) {
            console.warn('isModal is called without route!');
            return false;
        }
        if (!this.allRoutes.includes(route)) {
            console.warn('isModal is called with invalid route!');
            return false;
        }
        return !!((_d = (_c = (_b = (_a = getScreen(route)) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.presentation) === null || _c === void 0 ? void 0 : _c.toLowerCase()) === null || _d === void 0 ? void 0 : _d.includes('modal'));
    }
    whenCoordinatorIsReady(fn) {
        var _a;
        if ((_a = this.navigationContainer) === null || _a === void 0 ? void 0 : _a.isReady()) {
            return Promise.resolve(fn());
        }
        return new Promise(res => this.isReadyListeners.push(() => res(fn())));
    }
    executeWaitingScreens() {
        var _a, _b;
        if (this.waitingScreens.value.length <= 0) {
            return;
        }
        const value = this.waitingScreens.value[0];
        submitRouteParams(value.route, value.params);
        if (typeof value.index === 'number') {
            (_a = this.navigationContainer) === null || _a === void 0 ? void 0 : _a.reset({
                index: value.index,
                routes: [{ name: value.route, params: value.params }]
            });
        }
        else {
            (_b = this.navigationContainer) === null || _b === void 0 ? void 0 : _b.navigate(value.route, value.params);
        }
        this.waitingScreens.next(this.waitingScreens.value.slice(1));
        return this.executeWaitingScreens();
    }
    getWaitingRouteObject(route, params, index) {
        return { route, params, index };
    }
    hasWaitingRoute(route, params, index) {
        const value = JSON.stringify(this.getWaitingRouteObject(route, params, index));
        return !!this.waitingScreens.value.find(x => JSON.stringify(x) === value);
    }
    addWaitingRoute(route, params, index) {
        const value = this.getWaitingRouteObject(route, params, index);
        this.waitingScreens.next([...this.waitingScreens.value, value]);
        return new Promise((res) => {
            const sub = this.waitingScreens.subscribe(() => {
                if (!this.hasWaitingRoute(route, params, index)) {
                    sub.unsubscribe();
                    res(route);
                }
            });
        });
    }
    loadState(state) {
        var _a;
        (_a = state === null || state === void 0 ? void 0 : state.routes) === null || _a === void 0 ? void 0 : _a.forEach(route => {
            submitRouteParams(route.name, route.params);
            route.state && this.loadState(route.state);
        });
    }
    dismiss() {
        var _a;
        if (this.navigationContainer) {
            if (Platform.OS === 'web' && !this.navigationContainer.canGoBack()) {
                history.back();
            }
            else {
                (_a = this.navigationContainer) === null || _a === void 0 ? void 0 : _a.goBack();
            }
        }
        return this.getCurrentRoute();
    }
    getCurrentRouteParams() {
        var _a, _b, _c, _d;
        const route = this.getCurrentRoute();
        if (!this.navigationContainer && Platform.OS === 'web') {
            const params = queryString.parse(window.location.search);
            return {
                route,
                path: `${route}${window.location.search}`,
                params
            };
        }
        const params = ((_b = (_a = this.navigationContainer) === null || _a === void 0 ? void 0 : _a.getCurrentRoute()) === null || _b === void 0 ? void 0 : _b.params) || {};
        const query = queryString.stringify(params);
        return {
            route,
            path: `${route}${query ? '?' : ''}${query}`,
            params: ((_d = (_c = this.navigationContainer) === null || _c === void 0 ? void 0 : _c.getCurrentRoute()) === null || _d === void 0 ? void 0 : _d.params) || {}
        };
    }
    getPrevRoute() {
        return getPrevRoute().value;
    }
    getCurrentRoute() {
        var _a, _b;
        if (!this.navigationContainer && Platform.OS === 'web') {
            const parts = window.location.href.split('/');
            const route = parts[parts.length - 1].split('?')[0];
            return route || '';
        }
        return ((_b = (_a = this.navigationContainer) === null || _a === void 0 ? void 0 : _a.getCurrentRoute()) === null || _b === void 0 ? void 0 : _b.name) || '';
    }
    reset(index, route, params) {
        var _a;
        if (!this.allRoutes.includes(route)) {
            return this.navigateToNotLoadedRoute(route, params, index);
        }
        if (!((_a = this.navigationContainer) === null || _a === void 0 ? void 0 : _a.isReady())) {
            return this.addWaitingRoute(route, params, index);
        }
        if (this.waitingScreens.value.length > 0) {
            return this.addWaitingRoute(route, params, index);
        }
        submitRouteParams(route, params);
        const routeParams = this.routeParamsSterilization(route, params);
        return this.executeDismissMiddleware(route, routeParams)
            .then((result) => __awaiter(this, void 0, void 0, function* () {
            var _b, _c;
            const options = (_c = (_b = getScreen(result.name)) === null || _b === void 0 ? void 0 : _b.component) === null || _c === void 0 ? void 0 : _c.decoratorOptions;
            if (options) {
                const provider = Application.providers.loadProvider(options.provider);
                if (!(provider === null || provider === void 0 ? void 0 : provider.canUseSyncResolve())) {
                    yield (provider === null || provider === void 0 ? void 0 : provider.loadDependencies().catch(() => null));
                }
            }
            this.navigationContainer.reset({
                index,
                routes: [{ name: result.name, params: result.params }]
            });
            return this.getCurrentRoute();
        }));
    }
    navigate(route, params, forceReset) {
        if (!forceReset) {
            return this._navigate(route, params);
        }
        return this.reset(1, route, params);
    }
    replace(route, params) {
        var _a;
        if (!((_a = this.navigationContainer) === null || _a === void 0 ? void 0 : _a.isReady())) {
            return Promise.reject(new Error('Route ${route} can\'t be replaced until the navigation is ready!'));
        }
        const routeParams = this.routeParamsSterilization(route, params);
        submitRouteParams(route, params);
        this.navigationContainer.dispatch(StackActions.replace(route, routeParams));
        return Promise.resolve(route);
    }
    _navigate(route, params) {
        var _a;
        if (!this.allRoutes.includes(route)) {
            return this.navigateToNotLoadedRoute(route, params);
        }
        if (!((_a = this.navigationContainer) === null || _a === void 0 ? void 0 : _a.isReady())) {
            return this.addWaitingRoute(route, params);
        }
        if (this.waitingScreens.value.length > 0) {
            return this.addWaitingRoute(route, params);
        }
        const routeParams = this.routeParamsSterilization(route, params);
        submitRouteParams(route, params);
        return this.executeDismissMiddleware(route, routeParams)
            .then((result) => __awaiter(this, void 0, void 0, function* () {
            var _b, _c;
            const options = (_c = (_b = getScreen(result.name)) === null || _b === void 0 ? void 0 : _b.component) === null || _c === void 0 ? void 0 : _c.decoratorOptions;
            if (options) {
                const provider = Application.providers.loadProvider(options.provider);
                if (!(provider === null || provider === void 0 ? void 0 : provider.canUseSyncResolve())) {
                    yield (provider === null || provider === void 0 ? void 0 : provider.loadDependencies().catch(() => null));
                }
            }
            this.navigationContainer.navigate(result.name, result.params);
            return this.getCurrentRoute();
        }));
    }
    executeDismissMiddleware(route, params) {
        var _a, _b;
        const navigation_route = this.getCurrentRouteParams();
        const middleware = dismissMiddlewares.get(navigation_route.route || navigation_route.path || '');
        if (!middleware) {
            return Promise.resolve({ name: route, params });
        }
        const current_route = {
            name: navigation_route.route,
            params: navigation_route.params || {},
            options: (_a = getScreen(navigation_route.route)) === null || _a === void 0 ? void 0 : _a.options,
            is_modal: this.isModal(navigation_route.route)
        };
        const next_route = {
            name: route,
            params: params || {},
            options: (_b = getScreen(route)) === null || _b === void 0 ? void 0 : _b.options,
            is_modal: this.isModal(route)
        };
        return Promise.resolve()
            .then(() => middleware(current_route, next_route))
            .then(result => result || next_route);
    }
    addDismissMiddleware(route, middleware) {
        if (dismissMiddlewares.has(route)) {
            console.warn(`App already has middleware for ${route}! You will overwrite it!`);
        }
        dismissMiddlewares.set(route, middleware);
        return {
            remove: () => dismissMiddlewares.delete(route)
        };
    }
    getIsShowedRoute(route) {
        var _a;
        return this.findRoute(route, (_a = this.navigationContainer) === null || _a === void 0 ? void 0 : _a.getState());
    }
    navigateToNotLoadedRoute(route, params, index) {
        submitRouteParams(route, params);
        return new Promise((res, rej) => {
            const sub = getRoutes().subscribe(routes => {
                if (routes.includes(route)) {
                    sub.unsubscribe();
                    clearTimeout(timeout);
                    Promise.resolve()
                        .then(() => typeof index === 'number'
                        ? this.reset(index, route, params)
                        : this.navigate(route, params))
                        .then(res)
                        .catch(rej);
                }
            });
            const timeout = setTimeout(() => {
                sub.unsubscribe();
                rej(new Error(`Route ${route} is not loaded!`));
            }, 5000);
        });
    }
    routeParamsSterilization(route, params) {
        let hasFn = false;
        const routeParams = Object.entries(params || {}).reduce((acc, [key, value]) => {
            if (typeof value === 'function') {
                hasFn = true;
                return acc;
            }
            acc[key] = value;
            return acc;
        }, {});
        if (hasFn) {
            console.warn(`Route ${route} has function params!`);
        }
        return routeParams;
    }
    findRoute(route, state) {
        var _a;
        return !!((_a = state === null || state === void 0 ? void 0 : state.routes) === null || _a === void 0 ? void 0 : _a.find(navigationRoute => {
            if (navigationRoute.name === route) {
                return true;
            }
            if (!navigationRoute.state) {
                return false;
            }
            return this.findRoute(route, navigationRoute.state);
        }));
    }
}
